import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Alert, CircularProgress, Typography, Box, Divider,
  Table, TableBody, TableRow, TableCell, TableHead,
} from '@mui/material';
import type { Study, OrderInfo, PatientBean } from '../../api/types';
import { api } from '../../api/rpc';

interface PatientOrderHistoryDialogProps {
  open: boolean;
  study: Study | null;
  onClose: () => void;
}

export const PatientOrderHistoryDialog = ({ open, study, onClose }: PatientOrderHistoryDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [patient, setPatient] = useState<PatientBean | null>(null);
  const [order, setOrder] = useState<OrderInfo | null>(null);

  useEffect(() => {
    if (!open || !study) return;
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        const [pb, oi] = await Promise.all([
          study.patientKey ? api.getPatientBean(study.patientKey) : Promise.resolve(null),
          api.getOrder(study.objKey),
        ]);
        setPatient(pb);
        setOrder(oi);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, study]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Patient / Order History</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Patient info */}
            {patient && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Patient Information</Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: 140 }}>Name</TableCell>
                      <TableCell>{patient.niceName ?? patient.name}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: 140 }}>ID</TableCell>
                      <TableCell>{patient.id}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>DOB</TableCell>
                      <TableCell>{patient.dob ?? 'N/A'}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Sex</TableCell>
                      <TableCell>{patient.sex ?? 'N/A'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Site</TableCell>
                      <TableCell>{patient.siteName ?? patient.site}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>SSN</TableCell>
                      <TableCell>{patient.ssnNumber ? `***-**-${patient.ssnNumber.slice(-4)}` : 'N/A'}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Order info */}
            {order ? (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Order Information</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Field</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.commonOrder && (
                      <>
                        <TableRow>
                          <TableCell>Placer Order #</TableCell>
                          <TableCell>{order.commonOrder.placerOrderNumber ?? 'N/A'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Filler Order #</TableCell>
                          <TableCell>{order.commonOrder.fillerOrderNumber ?? 'N/A'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Order Status</TableCell>
                          <TableCell>{order.commonOrder.orderStatus ?? 'N/A'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Ordering Provider</TableCell>
                          <TableCell>{order.commonOrder.orderingProvider ?? 'N/A'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Order Date</TableCell>
                          <TableCell>{order.commonOrder.transactionDateTime ?? 'N/A'}</TableCell>
                        </TableRow>
                      </>
                    )}
                    {order.detailOrder && (
                      <>
                        <TableRow>
                          <TableCell>Procedure</TableCell>
                          <TableCell>
                            {order.detailOrder.procedureCodeDescr ?? order.detailOrder.universalServiceIdDescr ?? 'N/A'}
                            {order.detailOrder.procedureCode ? ` (${order.detailOrder.procedureCode})` : ''}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Priority</TableCell>
                          <TableCell>{order.detailOrder.priority ?? 'N/A'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Reason for Study</TableCell>
                          <TableCell>{order.detailOrder.reasonForStudy ?? 'N/A'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Clinical Info</TableCell>
                          <TableCell>{order.detailOrder.relevantClinicalInfo ?? 'N/A'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Result Status</TableCell>
                          <TableCell>{order.detailOrder.resultStatus ?? 'N/A'}</TableCell>
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No order information available for this study.
              </Typography>
            )}

            {/* Study info summary */}
            <Divider sx={{ my: 2 }} />
            {study && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Study Summary</Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: 140 }}>Accession</TableCell>
                      <TableCell>{study.acc}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: 140 }}>Date</TableCell>
                      <TableCell>{study.date}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Modality</TableCell>
                      <TableCell>{study.mod}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Body Part</TableCell>
                      <TableCell>{study.bod ?? 'N/A'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                      <TableCell colSpan={3}>{study.descr || 'N/A'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                      <TableCell>{study.status ?? 'N/A'}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Physician</TableCell>
                      <TableCell>{study.phyName ?? study.phy ?? 'N/A'}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
